<template>
    <div class="sso-entrada">
        <v-progress-circular v-if="!erro" indeterminate size="42" width="3" color="primary" />

        <p v-if="!erro" class="sso-entrada__texto">{{ $t('common.messages.info.openingSession') }}</p>

        <template v-else>
            <span class="material-symbols-outlined sso-entrada__icone">lock</span>
            <p class="sso-entrada__texto">{{ $t('common.messages.errors.ssoFailed') }}</p>
            <v-btn color="primary" variant="flat" to="/">
                {{ $t('common.topHeader.logout') }}
            </v-btn>
        </template>
    </div>
</template>

<script setup>
/**
 * Porta de entrada da personificação no admin.
 *
 * O admin sempre foi só EMISSOR de handoff (é onde se autentica; as outras
 * aplicações recebem a sessão dele -- ver services/sso.js). Esta é a primeira
 * vez que ele também RECEBE um: quem personifica no accounts é mandado para
 * cá já como a outra pessoa, porque o admin é onde se administra a
 * plataforma inteira, e ficar preso ao accounts limitaria o que dá para
 * testar.
 *
 * Só o formato de ticket -- ver `lerHandoffDeTicket` em services/sso.js.
 * De propósito não recebe usuário nem permissões pelo link: o `/me` e o
 * catálogo de permissões já são a fonte de verdade.
 */
import { onMounted, ref } from 'vue';
import router from '@/router';
import { trocarTicket, setUser, setAuth } from '@/services/api/auth.js';
import { lerHandoffDeTicket, limparFragmento } from '@/services/sso.js';
import { marcarAtor, lerAtorDoToken } from '@/services/personificacao.js';

const erro = ref(false);

onMounted(async () => {
    const ticket = lerHandoffDeTicket();

    if (!ticket) {
        erro.value = true;
        return;
    }

    try {
        const sessao = await trocarTicket(ticket);

        // Só o token: o `setUser()` logo abaixo busca o resto no `/me` --
        // a mesma fonte de verdade que qualquer outra entrada usa.
        setAuth({ token: sessao.access_token });

        // O ator sai do token novo, não de quem estava logado antes: se por
        // algum motivo a troca devolver 200 sem carregar personificação
        // nenhuma, não marcamos uma que não existe.
        marcarAtor(lerAtorDoToken(sessao.access_token));

        limparFragmento();

        await setUser();

        await router.replace(sessao.redirect_to || '/home');
    } catch (e) {
        console.error('Handoff de personificação recusado:', e);
        erro.value = true;
    }
});
</script>

<style scoped lang="scss">
.sso-entrada {
    min-height: 100vh;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 16px;

    &__texto {
        color: var(--secondaryColor);
        margin: 0;
    }

    &__icone {
        font-size: 40px;
    }
}
</style>
